import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {GET_HUB_LIST, GetHubListResponse} from '../../../wms/bags/graphql/queries/getHubList';
import {PopDisqualifyReasonOption} from '../graphql/types/PopReviewType';
import {
    GET_POP_DISQUALIFY_REASON_LIST,
    GetPopDisqualifyReasonListResponse
} from '../graphql/queries/getPopDisqualifyReasonList';
import {
    GET_POP_PICKUP_DRIVER_OPTIONS,
    GET_POP_PICKUP_SITE_OPTIONS,
    GetPopPickupDriverOptionsResponse,
    GetPopPickupSiteOptionsResponse
} from '../graphql/queries/getPopPickupOptions';

/**
 * POP 审核筛选区下拉选项（Hub / 不合格原因 / 揽收站点 / 揽收司机）
 *
 * 列表的名称类字段靠这些选项做 id→name 映射，故下拉必须是全量的；
 * 取数失败时服务端返回空数组，前端回落显示 ID。
 * 揽收站点按 Hub 查，未选 Hub 时不发起该查询。
 */

/** 下拉叶子项（可提交的实际选项） */
export interface ReasonOptionLeaf {
    value: string;
    label: string;
}

/** 下拉分组项（antd 嵌套 options，即 OptGroup；label 为分组标题，本身不可选） */
export interface ReasonOptionGroupWithChildren {
    label: string;
    options: ReasonOptionLeaf[];
}

/** 分组或未分组的顶层项 */
export type ReasonOptionGroup = ReasonOptionLeaf | ReasonOptionGroupWithChildren;

export interface OptionItem {
    id: string;
    name: string;
    code?: string;
    sortingCode?: string;
}

export interface PopReviewOptionsResult {
    /** 集散中心（Hub） */
    hubs: OptionItem[];
    /** 揽收站点（按当前选中 Hub 过滤；未选 Hub 时为空） */
    pickupSites: OptionItem[];
    /** 揽收司机 */
    pickupDrivers: OptionItem[];
    /** 站点 ID → 名称，供表格列与详情匹配 */
    pickupSiteNameById: Record<string, string>;
    /** 司机 account ID → 姓名，供表格列与详情匹配 */
    pickupDriverNameById: Record<string, string>;
    /** 不合格原因字典（服务端已排好序） */
    disqualifyReasons: PopDisqualifyReasonOption[];
    /** 原因码 → 当前语言文案 */
    reasonTextByCode: Record<string, string>;
    /** 按分类分组的原因下拉项；仍是扁平多选，选中的是叶子 code */
    reasonOptions: ReasonOptionGroup[];
    /** Hub ID → 名称，供表格列匹配 */
    hubNameById: Record<string, string>;
    loading: boolean;
    error?: any;
}

/**
 * @param hubId 当前选中的集散中心 ID；为空则不查揽收站点（站点从属于 Hub）
 */
export const usePopReviewOptions = (hubId?: string): PopReviewOptionsResult => {
    const {i18n} = useTranslation();
    const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

    // 与筛选区 PickupHubSelectFilter 复用同一 query，Apollo 缓存命中，不重复请求
    const {data: hubsData, loading: hubsLoading, error: hubsError} =
        useQuery<GetHubListResponse>(GET_HUB_LIST);
    const {
        data: reasonsData,
        loading: reasonsLoading,
        error: reasonsError
    } = useQuery<GetPopDisqualifyReasonListResponse>(GET_POP_DISQUALIFY_REASON_LIST);

    // 站点按 Hub 查：未选 Hub 时 skip，避免发无意义请求
    const {data: sitesData, loading: sitesLoading} = useQuery<GetPopPickupSiteOptionsResponse>(
        GET_POP_PICKUP_SITE_OPTIONS,
        {variables: {hubId: hubId || ''}, skip: !hubId}
    );
    const {data: driversData, loading: driversLoading} =
        useQuery<GetPopPickupDriverOptionsResponse>(GET_POP_PICKUP_DRIVER_OPTIONS);

    const hubs: OptionItem[] =
        hubsData?.hubList?.map(hub => ({
            id: hub.id?.toString(),
            name: hub.name,
            code: hub.sortingCode,
            sortingCode: hub.sortingCode
        })) || [];

    const hubNameById: Record<string, string> = {};
    hubs.forEach(hub => {
        if (hub.id) {
            hubNameById[hub.id] = hub.name;
        }
    });

    const pickupSites: OptionItem[] =
        sitesData?.getPopPickupSiteOptions?.map(site => ({id: site.id, name: site.name})) || [];
    const pickupDrivers: OptionItem[] =
        driversData?.getPopPickupDriverOptions?.map(driver => ({
            id: driver.driverId,
            name: driver.driverName
        })) || [];

    const pickupSiteNameById: Record<string, string> = {};
    pickupSites.forEach(site => {
        if (site.id) {
            pickupSiteNameById[site.id] = site.name;
        }
    });
    const pickupDriverNameById: Record<string, string> = {};
    pickupDrivers.forEach(driver => {
        if (driver.id) {
            pickupDriverNameById[driver.id] = driver.name;
        }
    });

    const disqualifyReasons = reasonsData?.getPopDisqualifyReasonList || [];

    // 文案单一来源为服务端枚举三语，按当前 locale 取；缺当前语言时回落 en，再回落首个，最后回落码值
    const reasonTextByCode: Record<string, string> = {};
    disqualifyReasons.forEach(reason => {
        if (!reason.code) {
            return;
        }
        const matched =
            reason.texts?.find(entry => entry.lang === currentLang) ||
            reason.texts?.find(entry => entry.lang === 'en') ||
            reason.texts?.[0];
        reasonTextByCode[reason.code] = matched?.text || reason.code;
    });

    /**
     * 不合格原因下拉项，按分类分组（antd 嵌套 options 即 OptGroup）。
     *
     * 仍是扁平多选：分组只是视觉分隔，选中与提交的都是叶子 code，分类不参与。
     * 服务端已排好序，故按出现顺序归并即可；categoryCode 为空时退化为不分组。
     */
    const reasonOptions: ReasonOptionGroup[] = [];
    const groupIndexByCategory = new Map<string, number>();
    disqualifyReasons
        .filter(reason => !!reason.code)
        .forEach(reason => {
            const code = reason.code as string;
            const leaf = {value: code, label: reasonTextByCode[code]};
            const categoryCode = reason.categoryCode || '';
            if (!categoryCode) {
                // 无分类：作为顶层选项直接挂出
                reasonOptions.push(leaf);
                return;
            }
            const existing = groupIndexByCategory.get(categoryCode);
            if (existing !== undefined) {
                (reasonOptions[existing] as ReasonOptionGroupWithChildren).options.push(leaf);
                return;
            }
            const categoryLabel =
                reason.categoryTexts?.find(entry => entry.lang === currentLang)?.text ||
                reason.categoryTexts?.find(entry => entry.lang === 'en')?.text ||
                reason.categoryTexts?.[0]?.text ||
                categoryCode;
            groupIndexByCategory.set(categoryCode, reasonOptions.length);
            reasonOptions.push({label: categoryLabel, options: [leaf]});
        });

    return {
        hubs,
        pickupSites,
        pickupDrivers,
        pickupSiteNameById,
        pickupDriverNameById,
        disqualifyReasons,
        reasonTextByCode,
        reasonOptions,
        hubNameById,
        loading: hubsLoading || reasonsLoading || sitesLoading || driversLoading,
        error: hubsError || reasonsError
    };
};
